import React from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { pick, startCase } from "lodash";

import AddressForm from "~/components/common/forms/AddressForm";
import { addressKeys } from "@caredna-marlin/utils/client/geocoding";
import { ProviderDetails } from "./userTypes";

export interface ProviderDetailsFormProps {
  provider: ProviderDetails;
  options: {
    providerTypes: { name: string; id: number }[];
    teams: { name: string; id: number }[];
    genders: { label: string; value: string }[];
    languages: { label: string; value: string }[];
    states: { label: string; value: string }[];
  };
  disabled: boolean;
  loading: boolean;
  onChange: (field: string, value: number[] | string[] | string | number | boolean) => void;
  onAddressChange: (address: Partial<ProviderDetails>) => void;
}

const ProviderDetailsForm: React.FC<ProviderDetailsFormProps> = ({
  provider,
  options,
  disabled,
  loading,
  onChange,
  onAddressChange,
}) => {
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    onChange(name, value);
  };

  const handleRecordInputChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    onChange(name, parseInt(value));
  };

  const handleRecordMultiSelectChange = (e: SelectChangeEvent<string[]>) => {
    const { name, value } = e.target;
    onChange(name, value);
  };

  const handleMultiSelectChange = (e: SelectChangeEvent<string[]>) => {
    const { name, value: rawValue } = e.target;
    const value = typeof rawValue === "string" ? rawValue.split(",") : rawValue;
    onChange(name, value);
  };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
      {/* Left Column */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <FormControl fullWidth required>
          <InputLabel>
            {options.providerTypes.length ? "Provider Type" : "Provider Type (not configured)"}
          </InputLabel>
          <Select
            name="provider.providerTypeId"
            label={options.providerTypes.length ? "Provider Type" : "Provider Type (not configured)"}
            value={provider.providerTypeId ? provider.providerTypeId.toString() : ""}
            style={{ backgroundColor: "white" }}
            disabled={disabled || options.providerTypes.length === 0}
            onChange={handleRecordInputChange}
            data-testid="provider-type-select"
          >
            {options.providerTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {startCase(type.name)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <AddressForm
          address={pick(provider, addressKeys)}
          setAddress={onAddressChange}
          disabled={loading || disabled}
          required
          skipLineTwo
          variant="outlined"
          sx={{ ".MuiFormControl-root": { margin: 0, backgroundColor: "white" } }}
        />

        <TextField
          label="Identifier"
          name="provider.identifier"
          value={provider.identifier ?? ""}
          style={{ backgroundColor: "white" }}
          disabled={disabled}
          onChange={handleInputChange}
          fullWidth
        />

        <FormControl fullWidth>
          <InputLabel>Licensed States</InputLabel>
          <Select
            multiple
            name="provider.licensedStates"
            label="Licensed States"
            value={provider.licensedStates}
            style={{ backgroundColor: "white" }}
            disabled={disabled}
            onChange={handleMultiSelectChange}
          >
            {options.states.map((state) => (
              <MenuItem key={state.value} value={state.value}>
                {state.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Right Column */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <FormControl fullWidth>
          <InputLabel>{options.teams.length ? "Teams" : "Teams (not configured)"}</InputLabel>
          <Select
            multiple
            name="provider.teams"
            //@ts-expect-error An integer[] is fine
            value={provider.teams}
            style={{ backgroundColor: "white" }}
            disabled={disabled || options.teams.length === 0}
            onChange={handleRecordMultiSelectChange}
          >
            {options.teams.map((team) => (
              <MenuItem key={team.id} value={team.id}>
                {team.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Gender</InputLabel>
          <Select
            name="provider.gender"
            label="Gender"
            value={provider.gender}
            style={{ backgroundColor: "white" }}
            disabled={disabled}
            onChange={handleInputChange}
          >
            {options.genders.map((gender) => (
              <MenuItem key={gender.label} value={gender.value}>
                {gender.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Languages</InputLabel>
          <Select
            multiple
            name="provider.languages"
            label="Languages"
            value={provider.languages}
            style={{ backgroundColor: "white" }}
            disabled={disabled}
            onChange={handleMultiSelectChange}
          >
            {options.languages.map((lang) => (
              <MenuItem key={lang.value} value={lang.value}>
                {lang.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              name="provider.external"
              checked={provider.external}
              disabled={disabled}
              onChange={() => onChange("provider.external", !provider.external)}
            />
          }
          label="External contractor"
        />
      </Box>
    </Box>
  );
};

export default ProviderDetailsForm;
